import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../api/axios";
import Select from "../component/SelectSearch";
import { toast } from "react-toastify";
import {
  createManualOrder,
  updateManualOrder,
  submitManualOrder,
  fetchManualOrderById,
  lookupPinCode,
  clearManualOrderError,
  clearManualOrderSuccess,
  clearCurrent,
  clearPinData,
  selectCurrentDraft,
  selectManualSaving,
  selectManualSubmitting,
  selectPinData,
  selectPinLoading,
  selectManualError,
  selectManualSuccess,
} from "../../../slice/manual/manualorderslice";

const IMAGE_BASE_URL = process.env.REACT_APP_API_URL;

// Safe image URL: blob/data/absolute URLs pass through; relative paths get base URL prepended
const getImageUrl = (path) => {
  if (!path) return "";
  if (
    path.startsWith("blob:") ||
    path.startsWith("data:") ||
    /^https?:\/\//i.test(path)
  ) {
    return path;
  }
  return `${IMAGE_BASE_URL}${path}`;
};

const EMPTY_FORM = {
  fullName:      "",
  phoneNumber:   "",
  streetAddress: "",
  pinCode:       "",
  district:      "",
  state:         "",
  orderNotes:    "",
  product:       "",
  noOfItems:     1,
  totalAmount:   "",
  paid:          false,
};

const ITEM_COUNT_OPTIONS = Array.from({ length: 20 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

// Only called on Submit — Draft bypasses this entirely
function validateForm(form) {
  const errors = {};
  if (!form.fullName.trim())
    errors.fullName = "Full Name is required";
  if (!form.phoneNumber.trim())
    errors.phoneNumber = "Phone Number is required";
  else if (!/^\d{10}$/.test(form.phoneNumber.trim()))
    errors.phoneNumber = "Phone Number must be exactly 10 digits";
  if (!form.streetAddress.trim())
    errors.streetAddress = "Street Address is required";
  if (!form.pinCode.trim())
    errors.pinCode = "PIN Code is required";
  else if (!/^\d{6}$/.test(form.pinCode.trim()))
    errors.pinCode = "PIN Code must be exactly 6 digits";
  if (!form.district.trim())
    errors.district = "District is required";
  if (!form.state.trim())
    errors.state = "State is required";
  if (!form.product)
    errors.product = "Product is required";
  if (!form.noOfItems || Number(form.noOfItems) < 1)
    errors.noOfItems = "Number of Items must be at least 1";
  if (!form.totalAmount || Number(form.totalAmount) <= 0)
    errors.totalAmount = "Total Amount must be greater than 0";
  return errors;
}

function FieldError({ name, errors }) {
  if (!errors[name]) return null;
  return (
    <div className="invalid-feedback d-block" style={{ fontSize: "0.8rem" }}>
      {errors[name]}
    </div>
  );
}

export default function ManualOrderCreate() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { id }     = useParams();

  const current    = useSelector(selectCurrentDraft);
  const saving     = useSelector(selectManualSaving);
  const submitting = useSelector(selectManualSubmitting);
  const pinData    = useSelector(selectPinData);
  const pinLoading = useSelector(selectPinLoading);
  const error      = useSelector(selectManualError);
  const successMsg = useSelector(selectManualSuccess);

  const [form, setForm]               = useState(EMPTY_FORM);
  const [products, setProducts]       = useState([]);
  const [imageFile, setImageFile]     = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [pinTimer, setPinTimer]       = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const fileRef = useRef();

  const productOptions = products.map((p) => ({
    value: p._id,
    label:
      p.status === false || p.status === "disabled"
        ? `${p.name} (Disabled)`
        : p.name,
  }));

  useEffect(() => {
    api.get("/api/products?limit=200")
      .then((r) => setProducts(r.data.data || r.data.products || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (id) dispatch(fetchManualOrderById(id));
    return () => {
      dispatch(clearCurrent());
      dispatch(clearPinData());
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (current && id) {
      setForm({
        fullName:      current.fullName      || "",
        phoneNumber:   current.phoneNumber   || "",
        streetAddress: current.streetAddress || "",
        pinCode:       current.pinCode       || "",
        district:      current.district      || "",
        state:         current.state         || "",
        orderNotes:    current.orderNotes    || "",
        product:       current.product?._id  || current.product || "",
        noOfItems:     current.noOfItems     || 1,
        totalAmount:   current.totalAmount   || "",
        paid:          current.paid          || false,
      });
      // Only restore server image if user hasn't selected a new local file
      if (current.image && !imageFile) {
        setImagePreview(getImageUrl(current.image));
      }
    }
  }, [current, id]); // intentionally exclude imageFile from deps

  useEffect(() => {
    if (pinData) {
      setForm((prev) => ({ ...prev, district: pinData.district, state: pinData.state }));
      setFieldErrors((prev) => ({ ...prev, district: undefined, state: undefined }));
    }
  }, [pinData]);

  useEffect(() => {
    if (successMsg) {
      toast.success(successMsg);
      dispatch(clearManualOrderSuccess());
    }
  }, [successMsg, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearManualOrderError());
    }
  }, [error, dispatch]);

  // Revoke blob URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (name === "pinCode") {
      clearTimeout(pinTimer);
      if (value.length === 6) {
        const t = setTimeout(() => dispatch(lookupPinCode(value)), 400);
        setPinTimer(t);
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Revoke previous blob URL to avoid memory leaks
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleImageRemove = () => {
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (imageFile) fd.append("image", imageFile);
    return fd;
  };

  // ── Save Draft: NO validation — partial saves are fully allowed ──
  const handleSave = async () => {
    setFieldErrors({});
    const fd = buildFormData();
    if (id) {
      const result = await dispatch(updateManualOrder({ id, formData: fd }));
      if (result.meta.requestStatus === "fulfilled") {
        toast.success("Draft saved successfully");
      }
    } else {
      const result = await dispatch(createManualOrder(fd));
      if (result.meta.requestStatus === "fulfilled") {
        toast.success("Draft saved successfully");
        navigate(`/admin/manual-order/edit/${result.payload._id}`, { replace: true });
      }
    }
  };

  // ── Submit Order: full validation required ──
  const handleSubmit = async () => {
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setFieldErrors({});

    const draftId = id || current?._id;

    if (!draftId) {
      const saveResult = await dispatch(createManualOrder(buildFormData()));
      if (saveResult.meta.requestStatus !== "fulfilled") return;
      dispatch(clearManualOrderSuccess());
      const submitResult = await dispatch(submitManualOrder(saveResult.payload._id));
      if (submitResult.meta.requestStatus === "fulfilled") navigate("/admin/order/Received");
      return;
    }

    const updateResult = await dispatch(updateManualOrder({ id: draftId, formData: buildFormData() }));
    if (updateResult.meta.requestStatus !== "fulfilled") return;
    dispatch(clearManualOrderSuccess());

    const submitResult = await dispatch(submitManualOrder(draftId));
    if (submitResult.meta.requestStatus === "fulfilled") navigate("/admin/order/Received");
  };

  const isEditing = !!id;
  const inputClass = (field) =>
    `form-control${fieldErrors[field] ? " is-invalid" : ""}`;

  return (
    <div className="row">
      <div className="col-12">
        <div className="card mt-3">

          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">
              {isEditing ? "Edit Draft" : "Create Manual Order"}
            </h4>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate("/admin/manual-order/list")}
            >
              ← Back to Drafts
            </button>
          </div>

          <div className="card-body">
            <div className="row g-3">

              <div className="col-md-6">
                <label className="form-label fw-semibold">Full Name <span className="text-danger">*</span></label>
                <input type="text" className={inputClass("fullName")} name="fullName"
                  value={form.fullName} onChange={handleChange} placeholder="Customer full name" />
                <FieldError name="fullName" errors={fieldErrors} />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Phone Number <span className="text-danger">*</span></label>
                <input type="text" className={inputClass("phoneNumber")} name="phoneNumber"
                  value={form.phoneNumber} onChange={handleChange}
                  placeholder="10-digit mobile number" maxLength={10} />
                <FieldError name="phoneNumber" errors={fieldErrors} />
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">Street Address <span className="text-danger">*</span></label>
                <input type="text" className={inputClass("streetAddress")} name="streetAddress"
                  value={form.streetAddress} onChange={handleChange} placeholder="House no, street, area" />
                <FieldError name="streetAddress" errors={fieldErrors} />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">PIN Code <span className="text-danger">*</span></label>
                <div className="input-group">
                  <input type="text" className={inputClass("pinCode")} name="pinCode"
                    value={form.pinCode} onChange={handleChange} maxLength={6} placeholder="6-digit PIN" />
                  {pinLoading && (
                    <span className="input-group-text">
                      <span className="spinner-border spinner-border-sm" />
                    </span>
                  )}
                </div>
                <FieldError name="pinCode" errors={fieldErrors} />
                <small className="text-muted">District &amp; State auto-fill on valid PIN</small>
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">District <span className="text-danger">*</span></label>
                <input type="text" className={inputClass("district")} name="district"
                  value={form.district} onChange={handleChange} placeholder="Auto-filled from PIN" />
                <FieldError name="district" errors={fieldErrors} />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">State <span className="text-danger">*</span></label>
                <input type="text" className={inputClass("state")} name="state"
                  value={form.state} onChange={handleChange} placeholder="Auto-filled from PIN" />
                <FieldError name="state" errors={fieldErrors} />
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">Order Notes</label>
                <textarea className="form-control" name="orderNotes"
                  value={form.orderNotes} onChange={handleChange}
                  rows={2} placeholder="Special instructions, customisation details, etc." />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Product <span className="text-danger">*</span></label>
                <Select
                  options={productOptions}
                  placeholder="Search & select product…"
                  value={productOptions.find((o) => o.value === form.product) || null}
                  onChange={(option) => {
                    setForm((prev) => ({ ...prev, product: option?.value || "" }));
                    if (fieldErrors.product)
                      setFieldErrors((prev) => ({ ...prev, product: undefined }));
                  }}
                  isClearable
                  className={fieldErrors.product ? "is-invalid" : ""}
                />
                <FieldError name="product" errors={fieldErrors} />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">No. of Items <span className="text-danger">*</span></label>
                <Select
                  options={ITEM_COUNT_OPTIONS}
                  placeholder="Select qty…"
                  value={ITEM_COUNT_OPTIONS.find((o) => o.value === Number(form.noOfItems)) || null}
                  onChange={(option) => {
                    setForm((prev) => ({ ...prev, noOfItems: option?.value || 1 }));
                    if (fieldErrors.noOfItems)
                      setFieldErrors((prev) => ({ ...prev, noOfItems: undefined }));
                  }}
                />
                <FieldError name="noOfItems" errors={fieldErrors} />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">Total Amount (₹) <span className="text-danger">*</span></label>
                <input type="number" className={inputClass("totalAmount")} name="totalAmount"
                  value={form.totalAmount} onChange={handleChange} placeholder="0" min={0} />
                <FieldError name="totalAmount" errors={fieldErrors} />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Image</label>
                <input type="file" className="form-control" accept="image/*"
                  ref={fileRef} onChange={handleImageChange} />
                {imagePreview && (
                  <div className="mt-2 d-flex align-items-center gap-2">
                    <img
                      src={imagePreview}
                      alt="preview"
                      style={{
                        width: 120, height: 120, objectFit: "cover",
                        borderRadius: 8, border: "1px solid #dee2e6",
                      }}
                      onError={(e) => {
                        if (!e.target.src.startsWith("blob:")) {
                          setImagePreview("");
                        }
                      }}
                    />
                    <button type="button" className="btn btn-sm btn-outline-danger"
                      onClick={handleImageRemove}>
                      Remove
                    </button>
                  </div>
                )}
              </div>

              <div className="col-md-6 d-flex align-items-end pb-1">
                <div>
                  <label className="form-label fw-semibold d-block">Paid</label>
                  <div
                    onClick={() => setForm((prev) => ({ ...prev, paid: !prev.paid }))}
                    style={{ width: 64, height: 32, borderRadius: 16,
                      background: form.paid ? "#22c55e" : "#ef4444",
                      cursor: "pointer", position: "relative",
                      transition: "background 0.25s", display: "inline-block" }}
                  >
                    <span style={{ position: "absolute", top: 4, left: form.paid ? 36 : 4,
                      width: 24, height: 24, borderRadius: "50%", background: "#fff",
                      transition: "left 0.25s", boxShadow: "0 1px 4px rgba(0,0,0,.25)" }} />
                  </div>
                  <span className="ms-2 fw-semibold" style={{ color: form.paid ? "#22c55e" : "#ef4444" }}>
                    {form.paid ? "Yes" : "No"}
                  </span>
                </div>
              </div>

            </div>
          </div>

          <div className="card-footer d-flex gap-2 justify-content-end">
            <button className="cuscancel" onClick={() => navigate("/admin/manual-order/list")}>
              Cancel
            </button>
            <button className="buttonwarning" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-1" /> Saving…</> : "💾 Save Draft"}
            </button>
            <button className="btn cusBTNPop btn-sm" onClick={handleSubmit} disabled={saving || submitting}>
              {submitting ? <><span className="spinner-border spinner-border-sm me-1" /> Submitting…</> : "🚀 Submit Order"}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}